import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Divider from '@precis-digital/kurama/Divider';
import Dropdown, { DropdownItem, DropdownList } from '@precis-digital/kurama/Dropdown';
import FilterIcon from '@precis-digital/kurama/icons/FilterIcon';
import Grid from '@precis-digital/kurama/Grid';
import MoreVerticalIcon from '@precis-digital/kurama/icons/MoreVerticalIcon';
import SortAscendingIcon from '@precis-digital/kurama/icons/SortAscendingIcon';
import SortDescendingIcon from '@precis-digital/kurama/icons/SortDescendingIcon';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import Toggle from '@precis-digital/kurama/Toggle';
import Typography from '@precis-digital/kurama/Typography';
import { type Theme } from '@precis-digital/kurama/theme';
import { useState, type ReactElement } from 'react';
import type { ChartDataRow } from '../../../types';
import { headerIconSize } from '../constants';
import { getSortDirectionForColumnId, isColumnWithAccessorKey } from '../utils';
import TableHeadCellPinningMenu from './TableHeadCellPinningMenu';
import { defaultTFunction, assertNever } from '../../../utils/generics';
import TableHeadCellFilteringMenu from './TableHeadCellFilteringMenu';
import type { ChartCardTableHeaderContextResolved } from '../tanstack';
import type { ColumnElementDescription } from '../types';
interface TableHeadCellMoreMenuProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends ChartCardTableHeaderContextResolved<DataKey, DataRow> {
  visible: boolean;
  onClose?: () => void;
}
const isDimensionFilterableColumn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(headerContext: ChartCardTableHeaderContextResolved<DataKey, DataRow>): boolean => {
  const {
    meta: contextMeta,
    column
  } = headerContext;
  if (column.columnDef.meta?.chartCard?.noFiltering === true) {
    return false;
  }
  if (!isColumnWithAccessorKey(column.columnDef)) {
    return false;
  }
  const elementDescription = contextMeta.chartCard.elementsDescriptionsMap[column.columnDef.accessorKey as DataKey] as ColumnElementDescription;
  if (elementDescription == null) {
    return true;
  }
  const {
    type
  } = elementDescription;
  switch (type) {
    case 'date':
      return contextMeta.chartCard.allowDateFiltering;
    case 'string':
    case 'variant':
      return true;
    case 'number':
    case 'currency':
    case 'percentage':
      return false;
    default:
      return assertNever(type);
  }
};
const getDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: Omit<TableHeadCellMoreMenuProps<DataKey, DataRow>, 'visible' | 'onClose'>, handleClose: () => void, theme: Theme): ReactElement[] => {
  const dropDownItems: ReactElement[] = [];
  const {
    meta: contextMeta,
    ...context
  } = props;
  const {
    getCanSort,
    id: columnId,
    columnDef: {
      meta: columnMeta
    }
  } = context.column;
  const {
    sorting,
    setSort,
    allowColumnFiltering,
    onOpenColumnFilter,
    allowColumnResizing,
    allowColumnAutoResizing,
    allowColumnPinning,
    colorScale,
    setDisplayColorScale,
    t = defaultTFunction
  } = contextMeta.chartCard;
  const fixedSort = columnMeta?.chartCard?.fixedSort ?? false;
  const canSort = getCanSort();
  const sorted = getSortDirectionForColumnId(sorting, columnId);
  const sortAscendingOnClick = (): void => {
    setSort?.(columnId, sorted === 'asc' ? false : 'asc');
    handleClose();
  };
  const sortDescendingOnClick = (): void => {
    setSort?.(columnId, sorted === 'desc' ? false : 'desc');
    handleClose();
  };
  const autosizeColumnOnClick = (): void => {
    handleClose();
  };
  const autosizeAllColumnsOnClick = (): void => {
    handleClose();
  };
  const colorScaleOnClick = (): void => {
    if (colorScale != null) {
      setDisplayColorScale?.(columnId, !colorScale.display);
    }
    handleClose();
  };
  if (canSort && !fixedSort) {
    dropDownItems.push(<DropdownItem key="sort_ascending" onClick={sortAscendingOnClick} leftIcon={<SortAscendingIcon color={sorted === 'asc' ? theme.tokens.foreground.main.link : undefined} />} tooltipProps={{
      kind: 'singleline',
      title: sorted === 'asc' ? t('sortNone') : t('sortAscending')
    }}>
        {t('sortAscending')}
      </DropdownItem>, <DropdownItem key="sort_descending" onClick={sortDescendingOnClick} leftIcon={<SortDescendingIcon color={sorted === 'desc' ? theme.tokens.foreground.main.link : undefined} />} tooltipProps={{
      kind: 'singleline',
      title: sorted === 'desc' ? t('sortNone') : t('sortDescending')
    }}>
        {t('sortDescending')}
      </DropdownItem>);
  }
  if (allowColumnFiltering && isDimensionFilterableColumn(props)) {
    if (onOpenColumnFilter != null) {
      dropDownItems.push(<Divider key="filter_divider" />, <DropdownItem key="filter_menu" leftIcon={<FilterIcon />} onClick={() => {
        onOpenColumnFilter(columnId);
        handleClose();
      }}>
          {t('filterData')}
        </DropdownItem>);
    } else {
      dropDownItems.push(<Divider key="filter_divider" />, <TableHeadCellFilteringMenu {...props} handleClose={handleClose} />);
    }
  }
  if (allowColumnPinning) {
    dropDownItems.push(<Divider key="pinning_divider" />, <TableHeadCellPinningMenu {...props} handleClose={handleClose} />);
  }
  if (allowColumnResizing && allowColumnAutoResizing) {
    dropDownItems.push(<Divider key="autosize_divider" />, <DropdownItem key="autosize_column" onClick={autosizeColumnOnClick}>
        {t('autosizeColumn')}
      </DropdownItem>, <DropdownItem key="autosize_all_columns" onClick={autosizeAllColumnsOnClick}>
        {t('autosizeAllColumns')}
      </DropdownItem>);
  }
  if (colorScale != null) {
    dropDownItems.push(<Divider key="color_scale_divider" />, <DropdownItem key="color_scale">
        <Grid display="inline-flex" justifyContent="space-between" gap={theme.spacing(3)} alignItems="center" width="100%" onClick={colorScaleOnClick}>
          <Typography key="text" variant="body1">
            {t('colorScale')}
          </Typography>
          <Toggle key="toggle" aria-label={t('colorScale')} name="colorScale" checked={colorScale.display} defaultChecked />
        </Grid>
      </DropdownItem>);
  }
  return dropDownItems;
};
const TableHeadCellMoreMenu = t0 => {
  const $ = _c(18);
  const {
    visible,
    onClose,
    ...props
  } = t0;
  const theme = useTheme();
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      event.stopPropagation();
      setDropdownAnchorEl(event.currentTarget);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleClick = t1;
  let t2;
  if ($[1] !== onClose) {
    t2 = () => {
      setDropdownAnchorEl(null);
      onClose?.();
    };
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleClose = t2;
  const dropDownItems = getDropDownItems(props, handleClose, theme);
  if (dropDownItems.length === 0 || !visible) {
    return null;
  }
  let t3;
  if ($[3] !== visible) {
    t3 = visible ? <MoreVerticalIcon {...headerIconSize} /> : <StyledPlaceholder {...headerIconSize} />;
    $[3] = visible;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const icon = t3;
  let t4;
  if ($[5] !== handleClick || $[6] !== icon) {
    t4 = <Button onClick={handleClick} size="small" variant="text" leftIcon={icon} />;
    $[5] = handleClick;
    $[6] = icon;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = dropdownAnchorEl != null;
  let t6;
  if ($[8] !== dropDownItems) {
    t6 = <DropdownList minWidth="300px">{dropDownItems}</DropdownList>;
    $[8] = dropDownItems;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== dropdownAnchorEl || $[11] !== handleClose || $[12] !== t5 || $[13] !== t6) {
    t7 = <Dropdown anchorEl={dropdownAnchorEl} open={t5} onClose={handleClose}>{t6}</Dropdown>;
    $[10] = dropdownAnchorEl;
    $[11] = handleClose;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t7) {
    t8 = <>{t4}{t7}</>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
const StyledPlaceholder = styled('div', {
  shouldForwardProp: prop => prop !== 'width' && prop !== 'height'
})<{
  width: number;
  height: number;
}>(({
  width,
  height
}) => ({
  width,
  height
}));
export default TableHeadCellMoreMenu;